import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Lock, Eye, EyeOff, ArrowRight, Building2, Users, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { VentureCard, VentureCardContent } from '@/components/ui/venture-card';
import { loginSchema } from '@/lib/validation';
import { logError } from '@/lib/logger';

type PortalType = 'client' | 'team' | null;

export default function Login() {
  const navigate = useNavigate();
  const [portalType, setPortalType] = useState<PortalType>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleBack = () => {
    setPortalType(null);
    setUsername('');
    setPassword('');
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate input with Zod (SEC-08, D-09)
    const validation = loginSchema.safeParse({ username, password });
    if (!validation.success) {
      const firstError = validation.error.errors[0]?.message ?? 'Invalid input';
      setError(firstError);
      setLoading(false);
      return;
    }

    try {
      if (portalType === 'client') {
        // CLIENT LOGIN FLOW - Look up email by VH username via SECURITY DEFINER function
        // (bypasses RLS since user is not authenticated yet)
        const { data: clients, error: lookupError } = await supabase
          .rpc('lookup_client_email', { p_username: username.toUpperCase() });

        const client = clients?.[0];
        if (lookupError || !client) {
          throw new Error('Invalid username or password');
        }

        const { error: authError } = await supabase.auth.signInWithPassword({
          email: client.email,
          password
        });

        if (authError) throw authError;

        // Persist account_type, client_id, and client_package in user_metadata (D-01)
        const { error: updateError } = await supabase.auth.updateUser({
          data: {
            account_type: 'client',
            client_id: client.id,
            client_package: client.package,
          }
        });

        if (updateError) {
          logError('error', 'Login', 'Failed to persist client metadata', {
            error: updateError.message,
            clientId: client.id,
          });
        }

        // Backfill user_id if null (existing clients created before auth linkage) (SEC-03)
        // Routed through client-user-id-backfill edge fn: the previous direct
        // UPDATE silently 0-row-updated under RLS because the SELECT/UPDATE
        // policy requires auth.uid() = user_id — which fails precisely when
        // user_id is still NULL. Edge fn uses service-role + validates the
        // caller JWT email matches. See RESEARCH §1.2 / FOUND-09.
        try {
          const { error: backfillError } = await supabase.functions.invoke(
            'client-user-id-backfill',
            { body: { email: client.email } },
          );
          if (backfillError) {
            // Non-fatal: login has already succeeded; log and continue.
            console.warn('client-user-id-backfill failed:', backfillError.message);
          }
        } catch (err) {
          console.warn('client-user-id-backfill threw:', err);
        }

        // Legacy localStorage writes (still used by some components, to be removed in Phase 7)
        localStorage.setItem('user_role', 'client');
        localStorage.setItem('account_type', 'client');
        localStorage.setItem('active_client_id', client.id);
        localStorage.setItem('client_package', client.package);

        const services = client.services_enabled || { demos: true };
        localStorage.setItem('client_services', JSON.stringify(services));

        if (!client.onboarding_complete) {
          toast.success('Welcome! Please complete your setup.');
          navigate('/client/onboarding');
          return;
        }

        localStorage.setItem('onboarding_complete', 'true');
        toast.success(`Welcome back, ${client.contact_name || client.company_name}!`);
        navigate('/dashboard');

      } else if (portalType === 'team') {
        // ===== TEAM/ADMIN LOGIN FLOW =====

        if (!username.includes('@')) {
          throw new Error('Team members must use email address');
        }

        // Normalize email to lowercase for consistent matching
        const normalizedEmail = username.toLowerCase().trim();

        // Step 1: Authenticate with Supabase
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password
        });

        if (authError) {
          throw authError;
        }

        // Step 2: Look up admin user in admin_users table (active only)
        const { data: adminUser, error: adminError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('email', normalizedEmail)
          .eq('status', 'active')
          .single();

        if (adminError || !adminUser) {
          await supabase.auth.signOut();
          throw new Error('Not authorized as team member');
        }

        // Phase 16-03: role-based routing (D-03, D-04).
        // admin_users.role is one of: super_admin | team_admin | member | setter | closer.
        const adminUsersRole = adminUser.role as
          | 'super_admin' | 'team_admin' | 'member' | 'setter' | 'closer';
        const isTeamRole = adminUsersRole === 'setter' || adminUsersRole === 'closer';
        const accountType: 'team' | 'admin' = isTeamRole ? 'team' : 'admin';

        // Persist role + account_type in user_metadata (D-01, D-04)
        const { error: updateError } = await supabase.auth.updateUser({
          data: {
            account_type: accountType,
            role: adminUsersRole,
          }
        });

        if (updateError) {
          // Non-fatal: metadata update failed, but auth succeeded
          // Log so failures are trackable and diagnosable
          logError('error', 'Login', 'Failed to persist admin metadata', {
            error: updateError.message,
            userId: authData.user?.id,
          });
        }

        // Legacy localStorage writes (still used by some components, to be removed in Phase 7).
        // admin_user JSON is load-bearing for ImpersonationBanner + CommandCenter — keep.
        localStorage.removeItem('active_client_id');
        localStorage.removeItem('client_package');
        localStorage.removeItem('client_services');
        localStorage.setItem('user_role', adminUsersRole);
        localStorage.setItem('admin_user', JSON.stringify(adminUser));
        localStorage.setItem('account_type', accountType);
        localStorage.setItem('onboarding_complete', 'true');

        // Route by role (D-03)
        let dest: string;
        if (adminUsersRole === 'setter') dest = '/setter/home';
        else if (adminUsersRole === 'closer') dest = '/closer/home';
        else dest = '/admin/command-center'; // super_admin | team_admin | member

        toast.success(`Welcome back, ${adminUser.full_name}!`);
        navigate(dest);
        return; // Stop execution
      }

    } catch (error: unknown) {
      if (import.meta.env.DEV) {
        console.error('Login error:', error);
      }
      const message = error instanceof Error ? error.message : 'Login failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // PORTAL SELECTION SCREEN
  if (!portalType) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-2xl"
        >
          {/* Header */}
          <motion.div
            className="flex flex-col items-center text-center space-y-4 mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Logo size="lg" />
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Venture Hills</h1>
              <p className="text-muted-foreground">
                Select your portal to continue
              </p>
            </div>
          </motion.div>

          {/* Portal Selection Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client Portal */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              onClick={() => setPortalType('client')}
              className="group bg-surface hover:bg-surface-elevated border-2 border-border hover:border-primary rounded-2xl p-8 text-left transition-all duration-300 glow-hover"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Building2 className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Client Portal</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Access your account, view demos, and track performance
              </p>
              <div className="flex items-center text-primary font-medium text-sm">
                Continue as client
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.button>

            {/* Team Portal */}
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              onClick={() => setPortalType('team')}
              className="group bg-surface hover:bg-surface-elevated border-2 border-border hover:border-secondary rounded-2xl p-8 text-left transition-all duration-300 glow-hover"
            >
              <div className="w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-secondary/20 transition-colors">
                <Users className="w-7 h-7 text-secondary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Team Portal</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Venture Hills team member and admin access
              </p>
              <div className="flex items-center text-secondary font-medium text-sm">
                Continue as team member
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  // LOGIN FORM (after portal selection)
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <VentureCard hover={false} className="p-8">
          <VentureCardContent className="space-y-8">
            {/* Header */}
            <motion.div
              className="flex flex-col items-center text-center space-y-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Logo size="lg" />
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">Venture Hills</h1>
                <p className="text-muted-foreground">
                  {portalType === 'client'
                    ? 'Sign in to your client portal'
                    : 'Sign in to Venture Hills admin portal'
                  }
                </p>
              </div>
            </motion.div>

            {/* Login Form */}
            <motion.form
              onSubmit={handleLogin}
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {portalType === 'client' ? 'Username' : 'Email Address'}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type={portalType === 'team' ? 'email' : 'text'}
                    value={username}
                    onChange={(e) => setUsername(
                      portalType === 'client'
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )}
                    className="w-full pl-10 pr-4 py-3 bg-surface border border-border rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors font-mono"
                    placeholder={portalType === 'client' ? 'VHACMEINC42' : 'your@email.com'}
                    required
                    autoComplete="username"
                  />
                </div>
                {portalType === 'client' && (
                  <p className="text-xs text-muted-foreground">
                    Enter your Venture Hills username
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-surface border border-border rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => navigate('/reset-password')}
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                variant="gradient"
                size="xl"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  'Signing in...'
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </motion.form>

            {/* Back Button */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to portal selection
              </button>
            </div>

            {/* Help Text (only for clients) */}
            {portalType === 'client' && (
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-xs text-center text-muted-foreground">
                  Your credentials were provided by your Venture Hills administrator
                </p>
              </div>
            )}
          </VentureCardContent>
        </VentureCard>
      </motion.div>
    </div>
  );
}
